import { useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { api } from "@/lib/apiClient";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  ArrowLeft,
  Wrench,
  AlertTriangle,
  Calendar,
  Clock,
  CheckCircle2,
  Plus,
  History,
  Settings,
  MapPin,
  Box,
  Ruler,
  Scale,
} from "lucide-react";
import { format, formatDistanceToNow, isBefore, addDays } from "date-fns";
import { es, enUS } from "date-fns/locale";
import { toast } from "sonner";
import { MoldMaintenanceDialog } from "./MoldMaintenanceDialog";
import { cn } from "@/lib/utils";

interface MoldDetailsProps {
  moldId: string;
  onBack: () => void;
}

interface MaintenanceRecord {
  id: string;
  maintenance_type: string;
  description: string | null;
  performed_at: string;
  performed_by: string | null;
  next_maintenance_date: string | null;
  parts_replaced: string | null;
  cost: number | null;
  duration_minutes: number | null;
  notes: string | null;
  performer?: {
    full_name: string | null;
    email: string;
  };
}

export function MoldDetails({ moldId, onBack }: MoldDetailsProps) {
  const { language } = useLanguage();
  const { user, userRole } = useAuth();
  const queryClient = useQueryClient();
  const locale = language === "es" ? es : enUS;
  const [maintenanceDialogOpen, setMaintenanceDialogOpen] = useState(false);

  // Fetch mold details
  const { data: mold, isLoading } = useQuery({
    queryKey: ["mold", moldId],
    queryFn: async () => {
      const { data, error } = await api
        .from("molds")
        .select("*")
        .eq("id", moldId)
        .single();

      if (error) throw error;
      return data;
    },
  });

  // Fetch maintenance history
  const { data: maintenanceHistory } = useQuery({
    queryKey: ["mold-maintenance", moldId],
    queryFn: async () => {
      const { data, error } = await api
        .from("mold_maintenance_history")
        .select(`
          *,
          performer:profiles!mold_maintenance_history_performed_by_fkey(full_name, email)
        `)
        .eq("mold_id", moldId)
        .order("performed_at", { ascending: false });

      if (error) throw error;
      return data as MaintenanceRecord[];
    },
  });

  // Fetch recent usage in process records
  const { data: recentUsage } = useQuery({
    queryKey: ["mold-usage", moldId],
    queryFn: async () => {
      const { data, error } = await api
        .from("process_records")
        .select(`
          id,
          status,
          started_at,
          completed_at,
          work_orders(order_number, product_name)
        `)
        .eq("mold_id", moldId)
        .order("created_at", { ascending: false })
        .limit(10);

      if (error) throw error;
      return data;
    },
  });

  // Reset uses mutation
  const resetUsesMutation = useMutation({
    mutationFn: async () => {
      const { error } = await api
        .from("molds")
        .update({ current_uses: 0 })
        .eq("id", moldId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mold", moldId] });
      toast.success("Contador de usos reiniciado");
    },
    onError: (error: any) => {
      toast.error(error.message || "Error al reiniciar contador");
    },
  });

  const canManage = userRole === "admin" || userRole === "supervisor";

  if (isLoading || !mold) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  const usagePercent = mold.max_uses
    ? Math.round((mold.current_uses / mold.max_uses) * 100)
    : 0;

  const needsMaintenance = mold.max_uses && mold.current_uses >= mold.max_uses;
  const nearMaintenance = mold.max_uses && mold.current_uses >= mold.max_uses * 0.8;

  const maintenanceOverdue = mold.next_maintenance_date &&
    isBefore(new Date(mold.next_maintenance_date), new Date());
  const maintenanceSoon = mold.next_maintenance_date &&
    isBefore(new Date(mold.next_maintenance_date), addDays(new Date(), 7));

  const getMaintenanceTypeLabel = (type: string) => {
    const types: Record<string, string> = {
      preventive: "Preventivo",
      corrective: "Correctivo",
      cleaning: "Limpieza",
      inspection: "Inspeccion",
      repair: "Reparacion",
      replacement: "Reemplazo",
    };
    return types[type] || type;
  };

  const getMaintenanceTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      preventive: "bg-blue-500",
      corrective: "bg-red-500",
      cleaning: "bg-green-500",
      inspection: "bg-yellow-500",
      repair: "bg-orange-500",
      replacement: "bg-purple-500",
    };
    return colors[type] || "bg-gray-500";
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold">{mold.name}</h1>
            {mold.code && (
              <Badge variant="outline">{mold.code}</Badge>
            )}
          </div>
          <p className="text-muted-foreground mt-1">{mold.description || "Sin descripcion"}</p>
        </div>
        <div className="flex items-center gap-2">
          {!mold.is_active && (
            <Badge variant="secondary">Inactivo</Badge>
          )}
          {needsMaintenance && (
            <Badge variant="destructive" className="gap-1">
              <AlertTriangle className="w-3 h-3" />
              Requiere Mantenimiento
            </Badge>
          )}
          {maintenanceOverdue && (
            <Badge variant="destructive" className="gap-1">
              <Calendar className="w-3 h-3" />
              Mantenimiento Vencido
            </Badge>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card className={cn(needsMaintenance && "border-red-500/50", nearMaintenance && !needsMaintenance && "border-orange-500/50")}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Ciclos de Uso</span>
              <Wrench className="w-4 h-4 text-muted-foreground" />
            </div>
            <p className="text-2xl font-bold">
              {mold.current_uses || 0}
              {mold.max_uses && <span className="text-muted-foreground text-lg">/{mold.max_uses}</span>}
            </p>
            {mold.max_uses && (
              <Progress
                value={usagePercent}
                className={cn(
                  "h-2 mt-2",
                  needsMaintenance && "[&>div]:bg-red-500",
                  nearMaintenance && !needsMaintenance && "[&>div]:bg-orange-500"
                )}
              />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Ultimo Mantenimiento</span>
              <History className="w-4 h-4 text-muted-foreground" />
            </div>
            <p className="text-lg font-medium">
              {mold.last_maintenance_date
                ? format(new Date(mold.last_maintenance_date), "PP", { locale })
                : "Sin registro"}
            </p>
            {mold.last_maintenance_date && (
              <p className="text-xs text-muted-foreground">
                {formatDistanceToNow(new Date(mold.last_maintenance_date), { locale, addSuffix: true })}
              </p>
            )}
          </CardContent>
        </Card>

        <Card className={cn(maintenanceOverdue && "border-red-500/50", maintenanceSoon && !maintenanceOverdue && "border-orange-500/50")}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Proximo Mantenimiento</span>
              <Calendar className="w-4 h-4 text-muted-foreground" />
            </div>
            <p className={cn(
              "text-lg font-medium",
              maintenanceOverdue && "text-red-500",
              maintenanceSoon && !maintenanceOverdue && "text-orange-500"
            )}>
              {mold.next_maintenance_date
                ? format(new Date(mold.next_maintenance_date), "PP", { locale })
                : "No programado"}
            </p>
            {mold.next_maintenance_date && (
              <p className={cn(
                "text-xs",
                maintenanceOverdue ? "text-red-500" : "text-muted-foreground"
              )}>
                {maintenanceOverdue ? "Vencido" : formatDistanceToNow(new Date(mold.next_maintenance_date), { locale, addSuffix: true })}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Total Mantenimientos</span>
              <Settings className="w-4 h-4 text-muted-foreground" />
            </div>
            <p className="text-2xl font-bold">{maintenanceHistory?.length || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Details & Actions */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Mold Details */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg">Especificaciones</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {mold.material && (
              <div className="flex items-start gap-3">
                <Box className="w-4 h-4 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">Material</p>
                  <p className="font-medium">{mold.material}</p>
                </div>
              </div>
            )}
            {mold.dimensions && (
              <div className="flex items-start gap-3">
                <Ruler className="w-4 h-4 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">Dimensiones</p>
                  <p className="font-medium">{mold.dimensions}</p>
                </div>
              </div>
            )}
            {mold.weight && (
              <div className="flex items-start gap-3">
                <Scale className="w-4 h-4 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">Peso</p>
                  <p className="font-medium">{mold.weight} kg</p>
                </div>
              </div>
            )}
            {mold.location && (
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">Ubicacion</p>
                  <p className="font-medium">{mold.location}</p>
                </div>
              </div>
            )}
            {mold.notes && (
              <div className="pt-4 border-t">
                <p className="text-sm text-muted-foreground mb-1">Notas</p>
                <p className="text-sm">{mold.notes}</p>
              </div>
            )}

            {canManage && (
              <div className="pt-4 border-t space-y-2">
                <Button
                  onClick={() => setMaintenanceDialogOpen(true)}
                  className="w-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Registrar Mantenimiento
                </Button>
                {mold.current_uses > 0 && (
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => resetUsesMutation.mutate()}
                  >
                    <History className="w-4 h-4 mr-2" />
                    Reiniciar Contador
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Maintenance History */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg">Historial de Mantenimiento</CardTitle>
          </CardHeader>
          <CardContent>
            {maintenanceHistory && maintenanceHistory.length > 0 ? (
              <div className="space-y-4">
                {maintenanceHistory.map((record) => (
                  <div
                    key={record.id}
                    className="flex gap-4 p-4 rounded-lg border hover:bg-muted/50 transition-colors"
                  >
                    <div className={cn(
                      "w-2 h-2 rounded-full mt-2",
                      getMaintenanceTypeColor(record.maintenance_type)
                    )} />
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium">
                            {getMaintenanceTypeLabel(record.maintenance_type)}
                          </p>
                          {record.description && (
                            <p className="text-sm text-muted-foreground mt-1">
                              {record.description}
                            </p>
                          )}
                        </div>
                        <span className="text-sm text-muted-foreground">
                          {format(new Date(record.performed_at), "PP", { locale })}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-4 mt-2 text-xs text-muted-foreground">
                        {record.performer && (
                          <span>Por: {record.performer.full_name || record.performer.email}</span>
                        )}
                        {record.duration_minutes && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {record.duration_minutes} min
                          </span>
                        )}
                        {record.cost && (
                          <span>${record.cost.toFixed(2)}</span>
                        )}
                      </div>
                      {record.parts_replaced && (
                        <p className="text-xs text-muted-foreground mt-2">
                          Piezas: {record.parts_replaced}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <History className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No hay registros de mantenimiento</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Usage */}
      {recentUsage && recentUsage.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Uso Reciente</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {recentUsage.map((usage: any) => (
                <div
                  key={usage.id}
                  className="flex items-center justify-between p-3 rounded-lg border"
                >
                  <div className="flex items-center gap-3">
                    {usage.status === "completed" ? (
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                    ) : (
                      <Clock className="w-4 h-4 text-blue-500" />
                    )}
                    <div>
                      <p className="font-medium">{usage.work_orders?.order_number}</p>
                      <p className="text-sm text-muted-foreground">{usage.work_orders?.product_name}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge variant={usage.status === "completed" ? "default" : "secondary"}>
                      {usage.status === "completed" ? "Completado" : "En proceso"}
                    </Badge>
                    {usage.completed_at && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {format(new Date(usage.completed_at), "Pp", { locale })}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Maintenance Dialog */}
      <MoldMaintenanceDialog
        open={maintenanceDialogOpen}
        onOpenChange={setMaintenanceDialogOpen}
        moldId={moldId}
        moldName={mold.name}
      />
    </div>
  );
}
